/**
 * Redux slice (src/store/slices/counsellorNotesSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";
import {
  createCounsellorInviteNote,
  getInviteNotes,
  getAllCounsellorInviteNotes,
  updateCounsellorInviteNote,
  deleteCounsellorInviteNote,
  togglePinCounsellorInviteNote,
} from "../actions/counsellorActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  allNotes: [],
  inviteNotes: {},
  noteLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalNotes: 0,
    notesPerPage: 50,
  },
};

/**
 * RTK slice definition: counsellorNotesSlice.
 */
const counsellorNotesSlice = createSlice({
  name: "counsellorNotes",
  initialState,
  reducers: {
    /**
     * Reducer: clearNotes.
     */
    clearNotes: (state) => {
      state.allNotes = [];
      state.inviteNotes = {};
    },
    /**
     * Reducer: clearError.
     */
    clearError: (state) => {
      state.error = null;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Create Note
      .addCase(createCounsellorInviteNote.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: createCounsellorInviteNote.fulfilled
      .addCase(createCounsellorInviteNote.fulfilled, (state, action) => {
        state.noteLoading = false;
        const note = action.payload;
        const inviteId = note.inviteId;
        if (!state.inviteNotes[inviteId]) {
          state.inviteNotes[inviteId] = [];
        }
        state.inviteNotes[inviteId].unshift(note);
        state.allNotes.unshift(note);
        toast.success(t("store.counsellorNotes.createdSuccessfully"));
      })
      // extraReducers handler: createCounsellorInviteNote.rejected
      .addCase(createCounsellorInviteNote.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellorNotes.createFailed"));
      })

      // Get Invite Notes
      .addCase(getInviteNotes.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: getInviteNotes.fulfilled
      .addCase(getInviteNotes.fulfilled, (state, action) => {
        state.noteLoading = false;
        const { inviteId } = action.meta.arg;
        state.inviteNotes[inviteId] = action.payload.notes || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      // extraReducers handler: getInviteNotes.rejected
      .addCase(getInviteNotes.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
      })

      // Get All Notes
      .addCase(getAllCounsellorInviteNotes.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: getAllCounsellorInviteNotes.fulfilled
      .addCase(getAllCounsellorInviteNotes.fulfilled, (state, action) => {
        state.noteLoading = false;
        state.allNotes = action.payload.notes || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      // extraReducers handler: getAllCounsellorInviteNotes.rejected
      .addCase(getAllCounsellorInviteNotes.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
      })

      // Update Note
      .addCase(updateCounsellorInviteNote.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: updateCounsellorInviteNote.fulfilled
      .addCase(updateCounsellorInviteNote.fulfilled, (state, action) => {
        state.noteLoading = false;
        const updatedNote = action.payload;
        const inviteId = updatedNote.inviteId;
        if (state.inviteNotes[inviteId]) {
          const idx = state.inviteNotes[inviteId].findIndex((n) => n._id === updatedNote._id);
          if (idx >= 0) {
            state.inviteNotes[inviteId][idx] = updatedNote;
          }
        }
        const allIdx = state.allNotes.findIndex((n) => n._id === updatedNote._id);
        if (allIdx >= 0) {
          state.allNotes[allIdx] = updatedNote;
        }
        toast.success(t("store.counsellorNotes.updatedSuccessfully"));
      })
      // extraReducers handler: updateCounsellorInviteNote.rejected
      .addCase(updateCounsellorInviteNote.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellorNotes.updateFailed"));
      })

      // Delete Note
      .addCase(deleteCounsellorInviteNote.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: deleteCounsellorInviteNote.fulfilled
      .addCase(deleteCounsellorInviteNote.fulfilled, (state, action) => {
        state.noteLoading = false;
        const noteId = action.payload;
        Object.keys(state.inviteNotes).forEach((inviteId) => {
          state.inviteNotes[inviteId] = state.inviteNotes[inviteId].filter((n) => n._id !== noteId);
        });
        state.allNotes = state.allNotes.filter((n) => n._id !== noteId);
        toast.success(t("store.counsellorNotes.deletedSuccessfully"));
      })
      // extraReducers handler: deleteCounsellorInviteNote.rejected
      .addCase(deleteCounsellorInviteNote.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellorNotes.deleteFailed"));
      })

      // Toggle Pin
      .addCase(togglePinCounsellorInviteNote.pending, (state) => {
        state.noteLoading = true;
      })
      // extraReducers handler: togglePinCounsellorInviteNote.fulfilled
      .addCase(togglePinCounsellorInviteNote.fulfilled, (state, action) => {
        state.noteLoading = false;
        const updatedNote = action.payload;
        const inviteId = updatedNote.inviteId;
        if (state.inviteNotes[inviteId]) {
          const idx = state.inviteNotes[inviteId].findIndex((n) => n._id === updatedNote._id);
          if (idx >= 0) {
            state.inviteNotes[inviteId][idx] = updatedNote;
          }
        }
        const allIdx = state.allNotes.findIndex((n) => n._id === updatedNote._id);
        if (allIdx >= 0) {
          state.allNotes[allIdx] = updatedNote;
        }
        toast.success(updatedNote.isPinned ? t("store.counsellorNotes.notePinned") : t("store.counsellorNotes.noteUnpinned"));
      })
      // extraReducers handler: togglePinCounsellorInviteNote.rejected
      .addCase(togglePinCounsellorInviteNote.rejected, (state, action) => {
        state.noteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearNotes, clearError } = counsellorNotesSlice.actions;
export default counsellorNotesSlice.reducer;
